import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { TableViewProps } from './table.types';
import Images from '@/assets/Images';
import { PAGE_SIZE_OPTIONS } from '@/config/constants';
import { format } from 'date-fns';
import { useTableView } from './hooks';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import { DATE_FORMATS } from '@/utils/constants';
import usePermissions from '@/hooks/usePermissions';

function TableView<T>({
  data,
  headerDetails,
  loading = false,
  emptyMessage = 'No records found',
  rowKey,
  onRowClick,
  maxHeight = 600,
  minWidth,
  renderToolbar,
  sortKey,
  sortDirection = 'asc',
  onSortChange,
  page = 0,
  rowsPerPage = 10,
  totalCount = 0,
  rowsPerPageOptions = PAGE_SIZE_OPTIONS,
  onPageChange,
  onRowsPerPageChange,
  disablePagination = false,
  handleView,
  handleEdit,
  handleDelete,
  confirmDelete,
  permissionModule,
}: TableViewProps<T>) {
  const { canView, canEdit, canDelete } = usePermissions();

  const hasViewPermission = !permissionModule || canView(permissionModule);
  const hasEditPermission = !permissionModule || canEdit(permissionModule);
  const hasDeletePermission = !permissionModule || canDelete(permissionModule);

  const showView = handleView && hasViewPermission;
  const showEdit = handleEdit && hasEditPermission;
  const showDelete = handleDelete && hasDeletePermission;
  const showActions = Boolean(showView || showEdit || showDelete);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] =
    useState<string | number | null>(null);

  const {
    dateFilterKey,
    pendingDateRange,
    numberRangeFilterKey,
    pendingNumberRange,
    setDateFilterKey,
    setPendingDateRange,
    setNumberRangeFilterKey,
    setPendingNumberRange,
    tableMinWidth,
    renderCellValue,
    getRowKey,
    getActionId,
    makeHeader,
    activeDateColumn,
    activeNumberRangeColumn,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useTableView({
    headerDetails,
    rowKey,
    onPageChange,
    onRowsPerPageChange,
    onSortChange,
    sortKey,
    sortDirection,
    minWidth,
    loading,
  });

  const handleDeleteClick = (actionId: string | number) => {
    if (!handleDelete) return;

    if (confirmDelete) {
      setPendingDeleteId(actionId);
      setDeleteDialogOpen(true);
      return;
    }

    handleDelete(actionId);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setPendingDeleteId(null);
  };

  const handleConfirmDelete = () => {
    if (pendingDeleteId == null || !handleDelete) return;

    handleDelete(pendingDeleteId);
    setDeleteDialogOpen(false);
    setPendingDeleteId(null);
  };

  return (
    <Box sx={{ mt: 3 }}>
      {renderToolbar}

      <TableContainer
        sx={{
          position: 'relative',
          maxHeight,
          overflow: 'auto',
          paddingRight: 0,
          scrollbarGutter: 'stable',
          '& .sticky-column-right': {
            position: 'sticky',
            right: 0,
            backgroundColor: '#fff',
            zIndex: 2,
          },
          '& .sticky-header-cell': {
            zIndex: 3,
            backgroundColor: '#fff',
          },
        }}
      >
        <Table
          sx={{
            tableLayout: 'auto',
            minWidth: tableMinWidth,
            '& th, & td': {
              padding: '8px 12px',
              whiteSpace: 'nowrap',
            },
          }}
        >
          <TableHead>
            <TableRow>
              {headerDetails.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align ?? 'left'}
                  className="sticky-header-cell"
                  sx={{
                    width: column.width ?? 'auto',
                    minWidth: column.minWidth ?? 'auto',
                  }}
                >
                  {makeHeader(column)}
                </TableCell>
              ))}

              {showActions && (
                <TableCell
                  align="center"
                  className="sticky-column-right sticky-header-cell"
                  sx={{
                    width: 90,
                    minWidth: 90,
                    padding: '8px 0px',
                  }}
                >
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {data.map((row, rowIndex) => {
              const actionId = getActionId(row, rowIndex);

              return (
                <TableRow
                  hover
                  key={getRowKey(row, rowIndex)}
                  onClick={() => onRowClick?.(row)}
                  sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                >
                  {headerDetails.map((column) => (
                    <TableCell
                      key={column.key}
                      align={column.align ?? 'left'}
                      sx={{
                        width: column.width ?? 'auto',
                        minWidth: column.minWidth ?? 'auto',
                      }}
                    >
                      {renderCellValue(column, row, rowIndex)}
                    </TableCell>
                  ))}

                  {showActions && (
                    <TableCell
                      align="center"
                      className="sticky-column-right"
                      sx={{
                        width: 90,
                        minWidth: 90,
                        padding: '4px',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        {showView && actionId != null && (
                          <Tooltip title="View">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleView(actionId);
                              }}
                            >
                              <img
                                src={Images.VIEW}
                                alt="View"
                                width="22"
                              />
                            </IconButton>
                          </Tooltip>
                        )}

                        {showEdit && actionId != null && (
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(actionId);
                              }}
                            >
                              <img
                                src={Images.EDIT}
                                alt="Edit"
                                width="22"
                              />
                            </IconButton>
                          </Tooltip>
                        )}

                        {showDelete && actionId != null && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteClick(actionId);
                              }}
                            >
                              <img
                                src={Images.DELETE}
                                alt="Delete"
                                width="22"
                              />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}

            {!loading && data.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={headerDetails.length + (showActions ? 1 : 0)}
                  align="center"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255,255,255,0.6)',
              zIndex: 4,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        )}
      </TableContainer>

      {activeDateColumn && (
        <Dialog
          open={Boolean(dateFilterKey)}
          onClose={() => setDateFilterKey(null)}
          maxWidth="xs"
        >
          <DialogTitle>Filter by {activeDateColumn.label}</DialogTitle>
          <DialogContent
            sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="From Date"
                format={DATE_FORMATS.INPUT}
                value={pendingDateRange[0]}
                onChange={(date) => {
                  setPendingDateRange([date as Date | null, pendingDateRange[1]]);
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    placeholder: DATE_FORMATS.INPUT,
                  },
                  actionBar: {
                    actions: ['clear'],
                  },
                }}
              />
              <DatePicker
                label="To Date"
                format={DATE_FORMATS.INPUT}
                value={pendingDateRange[1]}
                onChange={(date) => {
                  setPendingDateRange([pendingDateRange[0], date as Date | null]);
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    placeholder: DATE_FORMATS.INPUT,
                  },
                  actionBar: {
                    actions: ['clear'],
                  },
                }}
              />
            </LocalizationProvider>
          </DialogContent>
          <DialogActions>
            <Button
              size="small"
              onClick={() => {
                activeDateColumn.onFilterChange?.(['', '']);
                setPendingDateRange([null, null]);
                setDateFilterKey(null);
              }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                const nextValue: [string, string] = [
                  pendingDateRange[0]
                    ? format(pendingDateRange[0], 'yyyy-MM-dd')
                    : '',
                  pendingDateRange[1]
                    ? format(pendingDateRange[1], 'yyyy-MM-dd')
                    : '',
                ];
                activeDateColumn.onFilterChange?.(nextValue);
                setDateFilterKey(null);
              }}
            >
              Apply
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {activeNumberRangeColumn && (
        <Dialog
          open={Boolean(numberRangeFilterKey)}
          onClose={() => setNumberRangeFilterKey(null)}
          maxWidth="xs"
        >
          <DialogTitle>Filter by {activeNumberRangeColumn.label}</DialogTitle>
          <DialogContent
            sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}
          >
            <TextField
              label="From"
              type="number"
              size="small"
              fullWidth
              value={pendingNumberRange[0]}
              onChange={(e) => {
                setPendingNumberRange([e.target.value, pendingNumberRange[1]]);
              }}
              placeholder="Enter minimum value"
            />
            <TextField
              label="To"
              type="number"
              size="small"
              fullWidth
              value={pendingNumberRange[1]}
              onChange={(e) => {
                setPendingNumberRange([pendingNumberRange[0], e.target.value]);
              }}
              placeholder="Enter maximum value"
            />
          </DialogContent>
          <DialogActions>
            <Button
              size="small"
              onClick={() => {
                activeNumberRangeColumn.onFilterChange?.(['', '']);
                setPendingNumberRange(['', '']);
                setNumberRangeFilterKey(null);
              }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => {
                activeNumberRangeColumn.onFilterChange?.(pendingNumberRange);
                setNumberRangeFilterKey(null);
              }}
            >
              Apply
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {confirmDelete && (
        <ConfirmDeleteDialog
          open={deleteDialogOpen}
          title={confirmDelete.title}
          message={confirmDelete.message}
          confirmLabel={confirmDelete.confirmLabel}
          cancelLabel={confirmDelete.cancelLabel}
          disableBackdropClose={confirmDelete.disableBackdropClose}
          disableEscapeKeyDown={confirmDelete.disableEscapeKeyDown}
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {!disablePagination && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={rowsPerPageOptions}
          />
        </Box>
      )}
    </Box>
  );
}

export default TableView;